// Mobile/tablet comparison — Wayflyer is pinned as the second column and a
// pill toggle (or a horizontal swipe on the table) switches the third column
// between the alternatives. Keeps every row a side-by-side comparison instead
// of three long stacked cards the reader has to scroll between.
"use client";

import * as React from "react";

import { cn } from "@/lib/utils";

interface ComparisonMobileProps {
  /** Column headers; index 0 is Wayflyer. */
  columns: readonly string[];
  rows: ReadonlyArray<{ label: string; cells: readonly string[] }>;
  /** Label above the toggle, e.g. "Compare Wayflyer with". */
  toggleLabel: string;
  className?: string;
}

/** Minimum horizontal travel (px) before a touch counts as a swipe. */
const SWIPE_THRESHOLD = 40;

/** Question column slightly narrower; the two answer columns share the rest. */
const gridCols =
  "grid grid-cols-[minmax(0,0.85fr)_minmax(0,1.1fr)_minmax(0,1.05fr)]";

export function ComparisonMobile({
  columns,
  rows,
  toggleLabel,
  className,
}: ComparisonMobileProps) {
  const [wayflyer, ...alternatives] = columns;
  const [activeIndex, setActiveIndex] = React.useState(0);
  const touchStart = React.useRef<{ x: number; y: number } | null>(null);
  const tabRefs = React.useRef<(HTMLButtonElement | null)[]>([]);

  const select = (index: number) => {
    const clamped = Math.min(Math.max(index, 0), alternatives.length - 1);
    setActiveIndex(clamped);
  };

  const onTouchStart = (event: React.TouchEvent) => {
    const touch = event.touches[0];
    touchStart.current = touch ? { x: touch.clientX, y: touch.clientY } : null;
  };

  const onTouchEnd = (event: React.TouchEvent) => {
    const start = touchStart.current;
    touchStart.current = null;
    const end = event.changedTouches[0];
    if (start === null || end === undefined) return;
    const deltaX = end.clientX - start.x;
    const deltaY = end.clientY - start.y;
    // Ignore short moves and vertical/diagonal scrolls of the page.
    if (Math.abs(deltaX) < SWIPE_THRESHOLD) return;
    if (Math.abs(deltaX) <= Math.abs(deltaY)) return;
    select(deltaX < 0 ? activeIndex + 1 : activeIndex - 1);
  };

  const onTabKeyDown = (event: React.KeyboardEvent, index: number) => {
    if (event.key !== "ArrowRight" && event.key !== "ArrowLeft") return;
    event.preventDefault();
    const next = event.key === "ArrowRight" ? index + 1 : index - 1;
    const wrapped = (next + alternatives.length) % alternatives.length;
    select(wrapped);
    tabRefs.current[wrapped]?.focus();
  };

  const activeColumn = alternatives[activeIndex] ?? alternatives[0];
  const panelId = "restaurant-comparison-mobile-panel";
  const tabId = (index: number) => `${panelId}-tab-${index}`;

  return (
    <div className={cn("flex flex-col gap-4", className)}>
      <div className="flex flex-col gap-2">
        <p className="text-grey-500 text-[13px]">{toggleLabel}</p>
        <div
          role="tablist"
          aria-label={toggleLabel}
          className="flex flex-wrap gap-2"
        >
          {alternatives.map((column, i) => {
            const isActive = i === activeIndex;
            return (
              <button
                key={column}
                ref={(el) => {
                  tabRefs.current[i] = el;
                }}
                id={tabId(i)}
                type="button"
                role="tab"
                aria-selected={isActive}
                aria-controls={panelId}
                tabIndex={isActive ? 0 : -1}
                onClick={() => select(i)}
                onKeyDown={(event) => onTabKeyDown(event, i)}
                className={cn(
                  "h-9 rounded-full border px-4 text-[13px] font-medium transition-colors",
                  isActive
                    ? "border-grey-950 bg-grey-950 text-grey-0"
                    : "border-grey-100 bg-grey-0 text-grey-950 hover:border-grey-300"
                )}
              >
                {column}
              </button>
            );
          })}
        </div>
      </div>

      <div
        id={panelId}
        role="tabpanel"
        aria-labelledby={tabId(activeIndex)}
        className="border-grey-100 overflow-hidden rounded-[10px] border"
        onTouchStart={onTouchStart}
        onTouchEnd={onTouchEnd}
      >
        <div className={cn("bg-grey-0", gridCols)}>
          <div className="text-grey-500 px-2.5 py-3 text-[12px]">
            Common questions
          </div>
          <div className="p-1.5">
            <div className="text-grey-950 flex h-full items-center rounded-lg bg-blue-300 px-2.5 py-2 text-[13px] font-medium">
              {wayflyer}
            </div>
          </div>
          <div className="text-grey-950 flex items-center px-2.5 py-3 text-[13px] font-medium">
            {activeColumn}
          </div>
        </div>

        {rows.map((row) => (
          <div
            key={row.label}
            className={cn("border-grey-100 border-t", gridCols)}
          >
            <div className="text-grey-500 px-2.5 py-3 text-[12px] leading-[1.4]">
              {row.label}
            </div>
            <div className="text-grey-950 bg-blue-300/15 px-2.5 py-3 text-[13px] leading-[1.4] font-medium">
              {row.cells[0]}
            </div>
            <div className="text-grey-950 px-2.5 py-3 text-[13px] leading-[1.4]">
              {row.cells[activeIndex + 1]}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
